import React, { useState, useEffect } from 'react';
import { Article } from '../types';

const Findings: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  // Fallback data for when API isn't available yet
  const fallbackReports = [
    {
      id: 1,
      slug: "geneva-drift",
      report_id: "RPT-01-2024",
      status: "ACTIVE",
      title: "The Geneva Drift: Algorithmic Policy Contagion",
      date: "March 15, 2024",
      summary: "A forensic analysis of how international treaties influence domestic medical algorithms without public oversight.",
      image_url: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=2070",
      tags: "AI Governance,Regulatory Drift"
    },
    {
      id: 2,
      slug: "pharma-integrity",
      report_id: "RPT-02-2024",
      status: "ACTIVE",
      title: "Pharma-Integrity Audit: Hidden Weighting in Trials",
      date: "January 20, 2024",
      summary: "Identifying statistical anomalies in third-party clinical data sets that favor corporate sponsors over patient safety.",
      image_url: "https://images.unsplash.com/photo-1576086213369-97a306dca365?q=80&w=2040",
      tags: "Pharmaceutical,Clinical Trials"
    },
    {
      id: 3,
      slug: "agency-capture",
      report_id: "RPT-03-2023",
      status: "ACTIVE",
      title: "Agency Capture within Domestic Health Insurers",
      date: "December 05, 2023",
      summary: "Uncovering the 'soft-tissue' connections between regulatory personnel and insurance vendor decision-makers.",
      image_url: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?q=80&w=2070",
      tags: "Insurance,Regulatory Capture"
    },
    {
      id: 4,
      slug: "corporate-surveillance",
      report_id: "RPT-04-2023",
      status: "ACTIVE",
      title: "Corporate Surveillance & Worker Bias Data",
      date: "October 12, 2023",
      summary: "Testing of 'hidden weighting' in worker productivity AI that systematically penalizes older demographic data points.",
      image_url: "https://images.unsplash.com/photo-1454165833762-02651d9b008e?q=80&w=2070",
      tags: "Workplace AI,Discrimination"
    }
  ];

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch('/api/articles');
        if (response.ok) {
          const data = await response.json();
          setArticles(data);
        } else {
          // Use fallback data
          setArticles(fallbackReports);
        }
      } catch (error) {
        // Use fallback data if API not available
        setArticles(fallbackReports);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const displayArticles = articles.length > 0 ? articles : fallbackReports;

  return (
    <div className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mb-24">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400 mb-8">Investigative Archive</div>
          <h1 className="text-6xl font-black text-slate-900 mb-8 tracking-tighter">Forensic Inquiries &<br/><span className="text-slate-400">Audit Results</span></h1>
          <p className="text-xl text-slate-600 font-medium leading-relaxed">
            The formal publication of our investigative work. Each report is subject to rigorous forensic standards and double-blind human review before release.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-pulse text-slate-400">Loading investigations...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-24">
            {displayArticles.map(report => (
              <div key={report.id} className="group flex flex-col space-y-6">
                <div className="aspect-[16/9] overflow-hidden relative">
                  <img 
                    src={report.image_url} 
                    alt={report.title} 
                    className="w-full h-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0 group-hover:shadow-[0_0_50px_rgba(249,115,22,0.3)]"
                  />
                  <div className="absolute top-6 left-6 flex space-x-2">
                    <div className="bg-black text-white text-[9px] font-black px-3 py-1 uppercase tracking-widest">{report.report_id}</div>
                    <div className={`text-white text-[9px] font-black px-3 py-1 uppercase tracking-widest ${report.status === 'ACTIVE' ? 'bg-orange-600' : 'bg-slate-400'}`}>
                      {report.status}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">{report.date}</div>
                  <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-4 group-hover:text-orange-600 transition-colors">{report.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed font-medium mb-8">{report.summary}</p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {report.tags.split(',').map(tag => (
                      <span key={tag} className="text-[9px] font-bold uppercase tracking-widest bg-slate-100 px-3 py-1">{tag.trim()}</span>
                    ))}
                  </div>
                  <button className="text-[10px] font-black uppercase tracking-[0.2em] border-b-2 border-black pb-1 hover:text-slate-400 hover:border-slate-400 transition-all">Download Full PDF Briefing</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Findings;
